import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Stage, Layer, Line, Image as KonvaImage } from 'react-konva';
import { Arrow, Text as KonvaText } from 'react-konva';
import useImage from 'use-image';
import { getCurrentFreshness } from '^/types/__ResultType';
import { mergeSuccessResult } from '^/types/__ResultType';
import { AppContext } from '^/userWeb/types/AppContext';
import { GetCameraListAPISuccessResult } from '^/userWeb/apiTypes/getCamerasList';

type Props = {
  appContext: AppContext;
};

type ROI = { id: string; points: number[] };

// ROI 아이템 타입 정의
interface DetectionAreaItem {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
  type?: string;
  name?: string;
  ratio?: number;
}

interface DetectionAreaBlock {
  name: string;
  items: DetectionAreaItem[];
}

// detection_area_json 타입 지정
const detection_area_json: DetectionAreaBlock[] = [];

const CameraView = ({ camera, snapshotUrl }: { camera: any; snapshotUrl: string }) => {


  // === 통계 상태 ===
  type DayStat = {
    date: string;         // YYYY-MM-DD
    personIn: number;
    personOut: number;
    carIn: number;
    carOut: number;
    totalIn: number;
    totalOut: number;
  };

  const [stats, setStats] = useState<DayStat[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);


  // KST(+09:00)로 하루의 시작/끝 ISO 만들기
  const toKstISO = (d: Date, h: number, m: number, s: number) => {
    // 브라우저에서 로컬 타임존을 쓰되, +09:00 오프셋을 명시해서 보냄
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const HH = String(h).padStart(2, "0");
    const MM = String(m).padStart(2, "0");
    const SS = String(s).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}+09:00`;
  };

  const yyyymmdd = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };


  const buttonStyle = {
    padding: '10px 16px',
    marginRight: '10px',
    backgroundColor: '#4a90e2',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 500,
    cursor: 'pointer',
  };

  const completeButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#2ecc71',
  };
  useEffect(() => {
    console.log('📷 카메라 정보:', JSON.stringify(camera, null, 2));
  }, [camera]);


useEffect(() => {
  const fetchDaily = async (date: Date) => {
    const base = "https://hsg.ssnetworks.kr/test_api/counter_events";
    const phone = camera?.phone || camera?.userPhone; // 카메라 객체에 들어있는 폰 필드명 맞추세요
    if (!phone || !camera?.id) return null;

    const dateFrom = toKstISO(date, 0, 0, 0);
    const dateTo   = toKstISO(date, 23, 59, 59);

    const params = new URLSearchParams({
      phone,
      date_from: dateFrom,
      date_to: dateTo,
      cameraId: camera.id,      // 특정 카메라만
      with_summary: "true",
      page: "1",
      limit: "1",
    });

    const resp = await fetch(`${base}?${params.toString()}`);
    if (!resp.ok) throw new Error(`daily fetch failed ${resp.status}`);
    const data = await resp.json();

    // 요약 구조: summary.byType = [{type:"person", in:n, out:n}, {type:"car", in:n, out:n}]
    const byType: Array<{type: string; in: number; out: number}> = data?.summary?.byType ?? [];
    const totals = data?.summary?.totals ?? { in: 0, out: 0 };

    const person = byType.find((t) => t.type === "person") || { in: 0, out: 0 };
    const car    = byType.find((t) => t.type === "car")    || { in: 0, out: 0 };

    return {
      date: yyyymmdd(date),
      personIn: person.in || 0,
      personOut: person.out || 0,
      carIn: car.in || 0,
      carOut: car.out || 0,
      totalIn: totals.in || 0,
      totalOut: totals.out || 0,
    } as DayStat;
  };

  const run = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);

      const today = new Date();
      const d0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const d1 = new Date(d0); d1.setDate(d0.getDate() - 1);
      const d2 = new Date(d0); d2.setDate(d0.getDate() - 2);

      const results = await Promise.all([fetchDaily(d0), fetchDaily(d1), fetchDaily(d2)]);
      setStats(results.filter(Boolean) as DayStat[]);
    } catch (e: any) {
      console.error(e);
      setStatsError(e?.message ?? "통계 조회 실패");
    } finally {
      setStatsLoading(false);
    }
  };

  run();
}, [camera]);




useEffect(() => {
  if (camera?.detection_areas_json) {
    try {
      const parsed = JSON.parse(camera.detection_areas_json);
      const allRois: ROI[] = [];

      const detectionBlocks = Array.isArray(parsed) ? parsed : [parsed];

      detectionBlocks.forEach((block, blockIdx) => {
        // POLYGONS 영역 처리
        if (block.name === 'DEFAULT_DETECTION_AREA' && Array.isArray(block.items)) {
          const polygonPoints: number[] = [];

        block.items.forEach((item: DetectionAreaItem) => {
          const x = item.xmin * 640;
          const y = item.ymin * 480;
          polygonPoints.push(x, y);
        });
          allRois.push({
            id: `polygon-${blockIdx}`,
            points: polygonPoints,
          });
        }

        // 출입 방향 선
        if (block.name === 'DEFAULT_COUNTING_AREA' && Array.isArray(block.items)) {
            block.items.forEach((item: DetectionAreaItem, idx: number) => {
              const x1 = item.xmin * 640;
              const y1 = item.ymin * 480;
              const x2 = item.xmax * 640;
              const y2 = item.ymax * 480;

            allRois.push({
              id: `dir-${blockIdx}-${idx}`,
              points: [x1, y1, x2, y2],
            });
          });
        }
      });

      setRois(allRois);
    } catch (e) {
      console.error('❌ detection_areas_json 파싱 실패:', e);
    }
  }
}, [camera]);





  const [rois, setRois] = useState<ROI[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [drawingType, setDrawingType] = useState<'detect' | 'direction' | null>(null);
  const [newPoints, setNewPoints] = useState<number[]>([]);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  const [image] = useImage(snapshotUrl);
  const stageRef = useRef<any>(null);

  const handleStageClick = (e: any) => {
    if (!drawingType) return;

    // 이미 direction ROI가 존재한다면 무시
    if (drawingType === 'direction' && rois.some((roi) => roi.points.length === 4)) {
      alert('출입 방향 ROI는 하나만 생성할 수 있습니다.');
      return;
    }

    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    const updatedPoints = [...newPoints, pos.x, pos.y];
    setNewPoints(updatedPoints);

    if (drawingType === 'direction' && updatedPoints.length >= 4) {
      setRois([...rois, { id: Date.now().toString(), points: updatedPoints }]);
      setNewPoints([]);
      setDrawingType(null);
    }
  };

  const finishPolygon = () => {
    // 이미 detect ROI가 존재하면 추가 금지
    if (drawingType === 'detect' && rois.some((roi) => roi.points.length >= 6)) {
      alert('탐지 영역 ROI는 하나만 생성할 수 있습니다.');
      setNewPoints([]);
      setDrawingType(null);
      return;
    }

    if (drawingType === 'detect' && newPoints.length >= 6) {
      setRois([...rois, { id: Date.now().toString(), points: newPoints }]);
    }

    setNewPoints([]);
    setDrawingType(null);
  };

  const removeROI = (id: string) => {
    setRois(rois.filter((r) => r.id !== id));
  };

  const handleMouseMove = (e: any) => {
    const stage = stageRef.current;
    const pos = stage.getPointerPosition();
    if (pos) setMousePos({ x: pos.x, y: pos.y });
  };

useEffect(() => {
  if (!camera || rois.length === 0) return;

  // detection_areas_json 형식으로 변환
  const detection_area_json: any[] = [];

  const detectRoi = rois.find((roi) => roi.points.length >= 6);
  if (detectRoi) {
    const detectItems = [];
    for (let i = 0; i < detectRoi.points.length; i += 2) {
      detectItems.push({
        type: 'POLYGONS',
        xmin: detectRoi.points[i] / 640,
        ymin: detectRoi.points[i + 1] / 480,
        xmax: detectRoi.points[i] / 640,
        ymax: detectRoi.points[i + 1] / 480,
        name: `Area ${i / 2 + 1}`,
        ratio: 1.0,
      });
    }

    detection_area_json.push({
      name: 'DEFAULT_DETECTION_AREA',
      items: detectItems,
    });
  }

  const directionRoi = rois.find((roi) => roi.points.length === 4);
  if (directionRoi) {
    detection_area_json.push({
      name: 'DEFAULT_COUNTING_AREA',
      items: [
        {
          type: 'LINE',
          xmin: directionRoi.points[0] / 640,
          ymin: directionRoi.points[1] / 480,
          xmax: directionRoi.points[2] / 640,
          ymax: directionRoi.points[3] / 480,
          name: 'Counting Line',
          ratio: 1.0,
        },
      ],
    });
  }

  // API 전송
  const sendDetectionArea = async () => {
    try {
      await fetch('http://180.69.63.130:8000/api/cameras/update-detection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          camera_id: camera.id,
          detection_areas_json: detection_area_json,
        }),
      });
      console.log('✅ MongoDB ROI 정보 업로드 성공');
    } catch (error) {
      console.error('❌ ROI 정보 업로드 실패:', error);
    }
  };

  sendDetectionArea();
}, [rois]);


  return (


    
      <div style={{
        display: 'grid',
        gridTemplateColumns: '640px 1fr',
        gap: '30px',
        alignItems: 'start',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }}>
      {/* 영상 및 ROI 캔버스 */}
      <div>
        <Stage
          width={640}
          height={480}
          ref={stageRef}
          onClick={handleStageClick}
          onMouseMove={handleMouseMove}
        >
          <Layer>
            {image && <KonvaImage image={image} width={640} height={480} />}
            {/* 기존 ROI */}
            {rois.map((roi) => {
              const isDirection = roi.points.length === 4; // direction ROI: 2 points (4 values)
              const [x1, y1, x2, y2] = roi.points;

              return (
                <React.Fragment key={roi.id}>
                  {isDirection ? (
                  <>
                    {/* 입구 방향 화살표 (시작점 → 끝점) */}
                    <Arrow
                      points={[x1, y1, x2, y2]}
                      stroke="green"
                      strokeWidth={2}
                      pointerLength={10}
                      pointerWidth={10}
                    />

                    {/* 출구 방향 화살표 (끝점 → 시작점) - 반대 방향 */}
                    <Arrow
                      points={[x2, y2, x1, y1]}
                      stroke="green"
                      strokeWidth={2}
                      pointerLength={10}
                      pointerWidth={10}
                    />

                    {/* 출구 텍스트 */}
                    <KonvaText
                      x={x1 + 5}
                      y={y1 + 10}
                      text="출구"
                      fontSize={20}
                      fill="green"
                    />

                    {/* 입구 텍스트 */}
                    <KonvaText
                      x={x2 + 5}
                      y={y2 + 10}
                      text="입구"
                      fontSize={20}
                      fill="green"
                    />
                  </>
                                    ) : (
                    // 일반 detect ROI (polygon)
                    <Line
                      points={roi.points}
                      closed={roi.points.length >= 6}
                      stroke="red"
                      strokeWidth={2}
                    />


                    
                  )}
                </React.Fragment>
              );
            })}





            {/* 그리는 중인 선 */}
            {newPoints.length > 0 && mousePos && (
              <Line
                points={[...newPoints, mousePos.x, mousePos.y]}
                stroke="blue"
                strokeWidth={2}
                dash={[4, 4]}
              />
            )}
            {/* 마우스 커서 점 */}
            {mousePos && (
              <Line
                points={[mousePos.x, mousePos.y, mousePos.x + 1, mousePos.y + 1]}
                stroke="yellow"
                strokeWidth={6}
                lineCap="round"
              />
            )}
          </Layer>
        </Stage>
      </div>

      {/* ROI 리스트 및 버튼 */}
      <div>
        <h4>{camera.name} - ROI 목록</h4>
        <table style={{
          borderCollapse: 'collapse',
          width: '100%',
          marginTop: '5px',
          backgroundColor: '#fff',
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 0 8px rgba(0,0,0,0.05)'
        }}>
          <thead style={{ backgroundColor: '#f0f0f0' }}>
            <tr>
              <th style={{ padding: '5px', textAlign: 'left' }}>ID</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>좌표 수</th>
              <th style={{ padding: '5px' }}>삭제</th>
            </tr>
          </thead>
          <tbody>
            {rois.map((roi, index) => (
              <tr key={roi.id}>
                <td style={{ padding: '5px' }}>{`영역 ${index + 1}`}</td>
                <td style={{ padding: '5px' }}>{roi.points.length / 2}</td>
                <td style={{ padding: '5px', textAlign: 'center' }}>
                <button
                  style={{
                    ...buttonStyle,
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: '#e74c3c'
                  }}
                  onClick={() => removeROI(roi.id)}
                >
                  🗑️
                </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 버튼들: ROI 추가 */}
        <div style={{ marginTop: '10px' }}>
          {!drawingType ? (
            <>
              <button style={buttonStyle} onClick={() => setDrawingType('detect')}>탐지 영역 ROI 추가</button>{' '}
              <button style={buttonStyle} onClick={() => setDrawingType('direction')}>출입 방향 ROI 추가</button>
            </>
          ) : (
            <button style={completeButtonStyle} onClick={finishPolygon}>ROI 완료</button>
          )}
        </div>


        {/* === 3일 통계 테이블 === */}
        <div style={{ marginTop: 20 }}>
          <h4>{camera.name} - 최근 3일 출입/객체 통계</h4>

          {statsLoading && <p>통계 불러오는 중...</p>}
          {statsError && <p style={{ color: 'red' }}>{statsError}</p>}

          {!statsLoading && !statsError && (
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                marginTop: '10px',
                backgroundColor: '#fff',
                borderRadius: '6px',
                overflow: 'hidden',
                boxShadow: '0 0 8px rgba(0,0,0,0.05)',
              }}
            >
              <thead style={{ backgroundColor: '#f0f0f0' }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'left' }}>날짜</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Person IN</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Person OUT</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Car IN</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Car OUT</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Total IN</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Total OUT</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((row) => (
                  <tr key={row.date}>
                    <td style={{ padding: '10px' }}>{row.date}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.personIn}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.personOut}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.carIn}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.carOut}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.totalIn}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.totalOut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>



      </div>
    </div>
  );
};

export const CameraSettingsPage: React.FC<Props> = observer(({ appContext }) => {
  const [cameras, setCameras] = useState<
    GetCameraListAPISuccessResult extends { status: 'success'; value: infer T } ? T : never
  >([]);
  const [snapshotUrls, setSnapshotUrls] = useState<{ [cameraId: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const tokenResult = appContext.tokenRepo.userToken;
        if (tokenResult.status !== 'success') throw new Error('유저가 로그인되지 않았습니다.');
        const token = tokenResult.value;

        const freshness = getCurrentFreshness();

        const myInfoResult = appContext.myInfoCore.myInfo(token, freshness);
        if (myInfoResult.status !== 'success') throw new Error('유저 정보 불러오기 실패');

        // mergeSuccessResult 사용해서 [myInfo, token] 같이 반환받기
        const merged = mergeSuccessResult(myInfoResult, token);
        if (merged.status !== 'success') {
          throw new Error('유저 정보 병합 실패');
        }
        const [myInfo, userToken] = merged.value;

        const result = await appContext.apiClient.camera.getCamerasList.query({
          userId: myInfo.id,
          userToken: userToken,
          });
 
        if (result.status === 'failure') throw new Error('카메라 조회 실패');
        setCameras(result.value);
      } catch (err: any) {
        console.error(err);
        setError(err?.message ?? '카메라 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchCameras();
  }, [appContext]);


 
  
  useEffect(() => {
    const fetchSnapshots = async () => {
      for (const camera of cameras) {
        // ➕ "출입" 이름 포함된 카메라만 처리
        if (!camera.name.includes('출입')) continue;

        try {
          const response = await fetch('http://180.69.63.130:8000/api/cameras/snapshot', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rtsp_url: camera.cameraIp }),
          });

          if (!response.ok) throw new Error('스냅샷 요청 실패');
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);

          setSnapshotUrls((prev) => ({
            ...prev,
            [camera.id]: url,
          }));
        } catch (err) {
          console.error(`❌ [${camera.name}] 캡처 실패:`, err);
        }
      }
    };

    if (cameras.length > 0) fetchSnapshots();
  }, [cameras]);


  return (
    <div style={{ padding: '20px' }}>
      <h2>카메라 설정</h2>
      {loading && <p>불러오는 중...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {cameras
        .filter((camera) => camera.name.includes('출입'))
        .map((camera) => (
          snapshotUrls[camera.id] ? (
            <CameraView key={camera.id} camera={camera} snapshotUrl={snapshotUrls[camera.id]} />
          ) : (
            <p key={camera.id}>카메라 로딩중...</p>
          )
        ))}
    </div>
  );
});

export default CameraSettingsPage;
